import { createFileRoute } from '@tanstack/react-router'
import { useCompany } from '@/hooks/use-company'
import { localeFor } from '@/lib/locale'
import { useMemo, useState } from 'react'
import { Plus, Truck, PackageCheck, AlertTriangle, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useSuppliers, usePurchaseOrders, useReorderSuggestions,
  type POStatus, type ReorderSuggestion,
} from '@/hooks/use-purchasing'
import { useInventory } from '@/hooks/use-inventory'
import { money } from '@/lib/format'
import {
  Page, PageHeader, Card, CardHeader, Table, Trow, SkeletonRows, EmptyState,
  TabPills, btnPrimary, btnSecondary,
} from '@/components/rm'
import { Modal, Field, Input, Select } from '@/components/rm/form'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/purchasing')({ component: PurchasingPage })

const PO_COLS = '1fr 1.2fr 120px 120px 130px'
const STATUS_LABEL: Record<POStatus, string> = {
  draft: 'Draft', sent: 'Ordered', partial: 'Part-received', received: 'Received', cancelled: 'Cancelled',
}
const STATUS_TONE: Record<POStatus, string> = {
  draft: 'bg-canvas-subtle text-token-secondary',
  sent: 'bg-token-info-surface text-token-info',
  partial: 'bg-token-warning-surface text-token-warning',
  received: 'bg-token-success-surface text-token-success',
  cancelled: 'bg-token-danger-surface text-token-danger',
}

const fmtDate = (iso: string | null) =>
  iso ? phFormat(new Date(iso), 'date', { day: 'numeric', month: 'short' }) : '—'

function PurchasingPage() {
  const [tab, setTab] = useState('orders')
  const { orders, isLoading, isError, refetch, create, receive, setStatus } = usePurchaseOrders()
  const { suppliers, save: saveSupplier } = useSuppliers()
  const reorder = useReorderSuggestions()

  const [newPo, setNewPo] = useState(false)
  const [newSupplier, setNewSupplier] = useState(false)
  const [openPo, setOpenPo] = useState<string | null>(null)

  const outstanding = orders.filter((o) => o.status === 'sent' || o.status === 'partial')

  return (
    <Page>
      <PageHeader
        eyebrow="Purchasing"
        title="Purchase orders"
        subtitle="Order materials from your suppliers and receive them into stock."
        actions={
          <>
            <button className={btnSecondary} onClick={() => setNewSupplier(true)}>
              <Truck className="h-4 w-4" /> New supplier
            </button>
            <button className={btnPrimary} onClick={() => setNewPo(true)} disabled={!suppliers.length}>
              <Plus className="h-4 w-4" /> New order
            </button>
          </>
        }
      />

      <div className="mt-5">
        <TabPills
          active={tab}
          onChange={setTab}
          chips={[
            { id: 'orders', label: 'Orders', count: orders.length },
            { id: 'outstanding', label: 'Outstanding', count: outstanding.length },
            { id: 'reorder', label: 'Low stock', count: reorder.data?.length },
            { id: 'suppliers', label: 'Suppliers', count: suppliers.length },
          ]}
        />
      </div>

      <div className="mt-4">
        {tab === 'reorder' ? (
          <ReorderPanel
            rows={reorder.data ?? []}
            isLoading={reorder.isLoading}
            isError={reorder.isError}
            onRetry={() => reorder.refetch()}
            onOrder={async (supplierId, rows) => {
              const r = await create.mutateAsync({
                supplierId,
                lines: rows.map((s) => ({
                  description: s.name, qty: s.suggested_qty, unit_cost: s.unit_cost,
                  unit: s.unit, inventory_item_id: s.item_id, supplier_sku: s.supplier_sku,
                })),
              })
              toast.success('Draft order created')
              setTab('orders'); setOpenPo(r.poId)
            }}
          />
        ) : tab === 'suppliers' ? (
          <SuppliersPanel />
        ) : (
          <OrdersTable
            rows={tab === 'outstanding' ? outstanding : orders}
            isLoading={isLoading}
            isError={isError}
            onRetry={() => refetch()}
            onOpen={setOpenPo}
          />
        )}
      </div>

      {newPo && (
        <NewOrderModal
          onClose={() => setNewPo(false)}
          onCreate={async (input) => {
            const r = await create.mutateAsync(input)
            setNewPo(false); setOpenPo(r.poId)
            toast.success('Draft order created')
          }}
        />
      )}

      {newSupplier && (
        <SupplierModal
          onClose={() => setNewSupplier(false)}
          onSave={async (v) => { await saveSupplier.mutateAsync(v); setNewSupplier(false); toast.success('Supplier saved') }}
        />
      )}

      {openPo && (
        <OrderDetail
          poId={openPo}
          onClose={() => setOpenPo(null)}
          onReceive={async (lineId, qty) => {
            try {
              const r = await receive.mutateAsync({ lineId, qty })
              toast.success(r.po_status === 'received' ? 'Order fully received' : 'Stock received')
            } catch (e) {
              // The database refuses an over-receipt; show its message rather than a generic one,
              // because it says exactly how many are outstanding.
              toast.error(e instanceof Error ? e.message : 'Could not receive that')
            }
          }}
          onSend={async (id) => { await setStatus.mutateAsync({ id, status: 'sent' }); toast.success('Marked as ordered') }}
          onCancel={async (id) => { await setStatus.mutateAsync({ id, status: 'cancelled' }); toast.success('Order cancelled') }}
        />
      )}
    </Page>
  )
}

/* ---------------- orders ---------------- */

function OrdersTable({ rows, isLoading, isError, onRetry, onOpen }: {
  rows: ReturnType<typeof usePurchaseOrders>['orders']
  isLoading: boolean; isError: boolean; onRetry: () => void; onOpen: (id: string) => void
}) {
  if (isLoading) return <Card><SkeletonRows count={5} /></Card>
  // A failed load is shown as a failure. "No orders yet" on a network error would tell the trade person
  // nothing is on order when something might well be.
  if (isError) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
          <AlertTriangle className="h-5 w-5 text-token-danger" />
          <p className="text-[13px] text-token-secondary">Couldn’t load purchase orders.</p>
          <button className={btnSecondary} onClick={onRetry}>Try again</button>
        </div>
      </Card>
    )
  }
  if (!rows.length) return <Card><EmptyState label="No purchase orders yet." /></Card>

  return (
    <Table cols={PO_COLS} header={<><span>Order</span><span>Supplier</span><span>Expected</span><span>Total</span><span>Status</span></>}>
      {rows.map((o, i) => (
        <Trow key={o.id} cols={PO_COLS} last={i === rows.length - 1} onClick={() => onOpen(o.id)}>
          <span className="font-semibold text-token-primary">{o.po_number ?? '—'}</span>
          <span className="text-token-secondary">{o.supplier?.name ?? '—'}</span>
          <span className="text-token-secondary">{fmtDate(o.expected_at)}</span>
          <span className="tabular-nums text-token-primary">{money(o.total)}</span>
          <span>
            <span className={`inline-flex rounded-full px-2.5 py-1 text-[12px] font-bold ${STATUS_TONE[o.status]}`}>
              {STATUS_LABEL[o.status]}
            </span>
          </span>
        </Trow>
      ))}
    </Table>
  )
}

function OrderDetail({ poId, onClose, onReceive, onSend, onCancel }: {
  poId: string
  onClose: () => void
  onReceive: (lineId: string, qty: number) => Promise<void>
  onSend: (id: string) => Promise<void>
  onCancel: (id: string) => Promise<void>
}) {
  const { company } = useCompany()
  const { detail, isLoadingDetail, isErrorDetail } = usePurchaseOrders(poId)
  const [qty, setQty] = useState<Record<string, string>>({})

  return (
    <Modal open onClose={onClose} wide title={detail?.po.po_number ?? 'Purchase order'}>
      {isLoadingDetail ? (
        <SkeletonRows count={3} />
      ) : isErrorDetail || !detail ? (
        <p className="py-6 text-center text-[13px] text-token-danger">Couldn’t load this order.</p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-token-secondary">
            <span><span className="text-token-muted">Supplier</span> {detail.po.supplier?.name ?? '—'}</span>
            <span><span className="text-token-muted">Expected</span> {fmtDate(detail.po.expected_at)}</span>
            <span className={`inline-flex rounded-full px-2.5 py-1 text-[12px] font-bold ${STATUS_TONE[detail.po.status]}`}>
              {STATUS_LABEL[detail.po.status]}
            </span>
          </div>

          <div className="rounded-[12px] border border-token-subtle">
            {detail.lines.map((l, i) => {
              const outstanding = l.qty - l.qty_received
              return (
                <div key={l.id} className={`px-3.5 py-3 ${i < detail.lines.length - 1 ? 'border-b border-token-subtle' : ''}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[14px] font-medium text-token-primary">{l.description}</span>
                    <span className="tabular-nums text-[13px] text-token-secondary">
                      {l.qty_received} / {l.qty} {l.unit} · {money(l.unit_cost)}
                    </span>
                  </div>
                  {detail.po.status !== 'cancelled' && detail.po.status !== 'draft' && outstanding > 0 && (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        value={qty[l.id] ?? ''}
                        onChange={(e) => setQty((q) => ({ ...q, [l.id]: e.target.value }))}
                        inputMode="decimal"
                        placeholder={String(outstanding)}
                        className="h-8 w-24 rounded-lg border border-token-subtle px-2.5 text-[13px] outline-none focus:border-token-strong"
                      />
                      <button
                        className={btnSecondary}
                        onClick={async () => {
                          const v = parseFloat(qty[l.id] ?? '') || outstanding
                          await onReceive(l.id, v)
                          setQty((q) => ({ ...q, [l.id]: '' }))
                        }}>
                        <PackageCheck className="h-4 w-4" /> Receive
                      </button>
                      <span className="text-[12px] text-token-muted">{outstanding} outstanding</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="mt-3 flex items-center justify-between">
            <div className="text-[13px] text-token-secondary">
              Subtotal {money(detail.po.subtotal)} · {localeFor(company).taxLabel} {money(detail.po.tax)}
            </div>
            <div className="text-[15px] font-bold text-token-primary">{money(detail.po.total)}</div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            {detail.po.status === 'draft' && (
              <button className={btnPrimary} onClick={() => onSend(detail.po.id)}>Mark as ordered</button>
            )}
            {detail.po.status !== 'cancelled' && detail.po.status !== 'received' && (
              <button className={btnSecondary} onClick={() => onCancel(detail.po.id)}>Cancel order</button>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}

/* ---------------- new order ---------------- */

type DraftLine = { description: string; qty: string; unit_cost: string; inventory_item_id: string }

function NewOrderModal({ onClose, onCreate }: {
  onClose: () => void
  onCreate: (input: {
    supplierId: string
    lines: Array<{ description: string; qty: number; unit_cost: number; inventory_item_id?: string | null }>
    expectedAt?: string | null
  }) => Promise<void>
}) {
  const { suppliers } = useSuppliers()
  const { items } = useInventory()
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? '')
  const [expectedAt, setExpectedAt] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([{ description: '', qty: '1', unit_cost: '0', inventory_item_id: '' }])
  const [saving, setSaving] = useState(false)

  const total = useMemo(
    () => lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_cost) || 0), 0),
    [lines],
  )
  const valid = supplierId && lines.some((l) => l.description.trim() && (parseFloat(l.qty) || 0) > 0)

  const setLine = (i: number, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)))

  return (
    <Modal open onClose={onClose} wide title="New purchase order"
      footer={
        <>
          <button className={btnSecondary} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!valid || saving} onClick={async () => {
            setSaving(true)
            try {
              await onCreate({
                supplierId,
                expectedAt: expectedAt || null,
                lines: lines
                  .filter((l) => l.description.trim())
                  .map((l) => ({
                    description: l.description.trim(),
                    qty: parseFloat(l.qty) || 0,
                    unit_cost: parseFloat(l.unit_cost) || 0,
                    inventory_item_id: l.inventory_item_id || null,
                  })),
              })
            } catch (e) {
              toast.error(e instanceof Error ? e.message : 'Could not create the order')
            } finally { setSaving(false) }
          }}>
            {saving ? 'Creating…' : 'Create draft'}
          </button>
        </>
      }>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Supplier">
          <Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
        <Field label="Expected">
          <Input type="date" value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} />
        </Field>
      </div>

      <div className="mt-4 space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="rounded-[10px] border border-token-subtle p-2">
            {/* Picking a stock item is what lets receiving move stock into inventory. A free-text
                line still orders fine — it just has nowhere to land when it arrives. */}
            <Select value={l.inventory_item_id} onChange={(e) => {
              const it = items.find((x) => x.id === e.target.value)
              setLine(i, {
                inventory_item_id: e.target.value,
                description: it ? it.name : l.description,
                unit_cost: it ? String(it.unit_cost) : l.unit_cost,
              })
            }}>
              <option value="">— not a stock item —</option>
              {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
            </Select>
            <div className="mt-2 grid grid-cols-[1fr_70px_90px_32px] items-center gap-2">
              <Input value={l.description} placeholder="Description"
                onChange={(e) => setLine(i, { description: e.target.value })} />
              <Input value={l.qty} inputMode="decimal" placeholder="Qty"
                onChange={(e) => setLine(i, { qty: e.target.value })} />
              <Input value={l.unit_cost} inputMode="decimal" placeholder="Cost"
                onChange={(e) => setLine(i, { unit_cost: e.target.value })} />
              <button className="text-token-muted hover:text-token-danger"
                onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls))}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        <button className={btnSecondary} onClick={() => setLines((ls) => [...ls, { description: '', qty: '1', unit_cost: '0', inventory_item_id: '' }])}>
          <Plus className="h-3.5 w-3.5" /> Add line
        </button>
      </div>

      <div className="mt-3 text-right text-[13.5px] font-bold text-token-primary">
        {money(total)} <span className="font-normal text-token-muted">ex VAT</span>
      </div>
    </Modal>
  )
}

/* ---------------- reorder ---------------- */

function ReorderPanel({ rows, isLoading, isError, onRetry, onOrder }: {
  rows: ReorderSuggestion[]
  isLoading: boolean; isError: boolean; onRetry: () => void
  onOrder: (supplierId: string, rows: ReorderSuggestion[]) => Promise<void>
}) {
  if (isLoading) return <Card><SkeletonRows count={4} /></Card>
  if (isError) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
          <AlertTriangle className="h-5 w-5 text-token-danger" />
          <p className="text-[13px] text-token-secondary">Couldn’t work out what’s low on stock.</p>
          <button className={btnSecondary} onClick={onRetry}>Try again</button>
        </div>
      </Card>
    )
  }
  if (!rows.length) return <Card><EmptyState label="Nothing is below its reorder point." /></Card>

  // Grouped by supplier because that is how orders get placed — one order per supplier, not one
  // per item.
  const groups = new Map<string, { name: string; rows: ReorderSuggestion[] }>()
  const unassigned: ReorderSuggestion[] = []
  for (const r of rows) {
    if (!r.supplier_id) { unassigned.push(r); continue }
    const g = groups.get(r.supplier_id) ?? { name: r.supplier_name ?? 'Supplier', rows: [] }
    g.rows.push(r); groups.set(r.supplier_id, g)
  }

  return (
    <div className="space-y-4">
      {[...groups.entries()].map(([supplierId, g]) => (
        <Card key={supplierId}>
          <CardHeader
            title={g.name}
            count={g.rows.length}
            action={
              <button className={btnPrimary} onClick={() => onOrder(supplierId, g.rows)}>
                <Plus className="h-4 w-4" /> Order all
              </button>
            }
          />
          {g.rows.map((r, i) => (
            <div key={r.item_id} className={`flex items-center justify-between px-5 py-3 ${i < g.rows.length - 1 ? 'border-b border-token-subtle' : ''}`}>
              <div>
                <div className="text-[14px] font-medium text-token-primary">{r.name}</div>
                <div className="text-[12.5px] text-token-secondary">
                  {r.qty_on_hand} on hand · reorder at {r.reorder_point}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[13.5px] font-bold text-token-primary">{r.suggested_qty} {r.unit}</div>
                <div className="text-[12.5px] text-token-secondary">{money(r.unit_cost)} ea</div>
              </div>
            </div>
          ))}
        </Card>
      ))}

      {unassigned.length > 0 && (
        <Card>
          <CardHeader title="No supplier set" count={unassigned.length} />
          <p className="px-5 pb-4 pt-1 text-[12.5px] text-token-secondary">
            These are low but have no supplier on file, so there’s nobody to order them from. Add
            one to the item’s catalogue, or set a preferred supplier.
          </p>
          {unassigned.map((r) => (
            <div key={r.item_id} className="border-t border-token-subtle px-5 py-3 text-[13.5px] text-token-primary">
              {r.name} <span className="text-token-secondary">· {r.qty_on_hand} on hand</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  )
}

/* ---------------- suppliers ---------------- */

function SuppliersPanel() {
  const { suppliers, isLoading, isError, refetch, save, archive } = useSuppliers()
  const [edit, setEdit] = useState<string | null>(null)

  if (isLoading) return <Card><SkeletonRows count={4} /></Card>
  if (isError) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
          <AlertTriangle className="h-5 w-5 text-token-danger" />
          <p className="text-[13px] text-token-secondary">Couldn’t load suppliers.</p>
          <button className={btnSecondary} onClick={() => refetch()}>Try again</button>
        </div>
      </Card>
    )
  }
  if (!suppliers.length) return <Card><EmptyState label="No suppliers yet." /></Card>

  const current = suppliers.find((s) => s.id === edit) ?? null

  return (
    <>
      <Card>
        {suppliers.map((s, i) => (
          <div key={s.id} className={`flex items-center justify-between px-5 py-3.5 ${i < suppliers.length - 1 ? 'border-b border-token-subtle' : ''}`}>
            <div>
              <div className="text-[14px] font-medium text-token-primary">{s.name}</div>
              <div className="text-[12.5px] text-token-secondary">
                {[s.contact_name, s.phone, s.email].filter(Boolean).join(' · ') || 'No contact details'}
              </div>
            </div>
            {/* Touch-safe: never hide row actions behind hover. */}
            <div className="flex items-center gap-2">
              <button className={btnSecondary} onClick={() => setEdit(s.id)}>Edit</button>
              <button className="text-token-muted hover:text-token-danger"
                onClick={async () => { await archive.mutateAsync(s.id); toast.success('Supplier archived') }}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </Card>

      {current && (
        <SupplierModal
          initial={current}
          onClose={() => setEdit(null)}
          onSave={async (v) => { await save.mutateAsync({ ...v, id: current.id }); setEdit(null); toast.success('Supplier saved') }}
        />
      )}
    </>
  )
}

function SupplierModal({ initial, onClose, onSave }: {
  initial?: { name: string; contact_name: string | null; email: string | null; phone: string | null; account_number: string | null }
  onClose: () => void
  onSave: (v: { name: string; contact_name: string | null; email: string | null; phone: string | null; account_number: string | null }) => Promise<void>
}) {
  const [v, setV] = useState({
    name: initial?.name ?? '', contact_name: initial?.contact_name ?? '',
    email: initial?.email ?? '', phone: initial?.phone ?? '', account_number: initial?.account_number ?? '',
  })
  const [saving, setSaving] = useState(false)

  return (
    <Modal open onClose={onClose} title={initial ? 'Edit supplier' : 'New supplier'}
      footer={
        <>
          <button className={btnSecondary} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!v.name.trim() || saving} onClick={async () => {
            setSaving(true)
            try {
              await onSave({
                name: v.name.trim(),
                contact_name: v.contact_name.trim() || null,
                email: v.email.trim() || null,
                phone: v.phone.trim() || null,
                account_number: v.account_number.trim() || null,
              })
            } catch (e) {
              toast.error(e instanceof Error ? e.message : 'Could not save the supplier')
            } finally { setSaving(false) }
          }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }>
      <div className="space-y-3">
        <Field label="Name"><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
        <Field label="Contact"><Input value={v.contact_name} onChange={(e) => setV({ ...v, contact_name: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><Input value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></Field>
          <Field label="Account no."><Input value={v.account_number} onChange={(e) => setV({ ...v, account_number: e.target.value })} /></Field>
        </div>
        <Field label="Email"><Input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>
      </div>
    </Modal>
  )
}
